import type { FC } from '../../lib/teact/teact';
import {
  memo, useCallback, useEffect, useRef, useState,
} from '../../lib/teact/teact';
import { getActions } from '../../global';

import { TEST_IDS } from '../../lib/testids';
import {
  buildWorkGoogleBridgeSaasReturnUrl,
  buildSaasGoogleAuthBridgeUrl,
  buildSaasGoogleOwnerAuthTargetUrl,
  isWorkGoogleBridgeCallback,
  mountGoogleSignInButton,
  readCanonicalWorkGoogleBridgeTransactionTruth,
  readWorkGoogleBridgeCallbackTransactionId,
  resolveGoogleOauthClientId,
  shouldAutoPromptGoogleAuth,
  shouldShowGoogleAuth,
  shouldUseSaasGoogleOwnerAuth,
} from '../../util/googleAuth';
import { buildStorageSessionRequestBody } from '../../util/saasHandoffIntent';

import styles from './AuthGoogle.module.scss';

const GOOGLE_CLIENT_ID = resolveGoogleOauthClientId(
  {
    GOOGLE_OAUTH_CLIENT_ID: process.env.GOOGLE_OAUTH_CLIENT_ID,
    EASYCEO_GOOGLE_OAUTH_CLIENT_ID: process.env.EASYCEO_GOOGLE_OAUTH_CLIENT_ID,
    RUNAI_GOOGLE_OAUTH_CLIENT_ID: process.env.RUNAI_GOOGLE_OAUTH_CLIENT_ID,
  },
);

type OwnProps = {
  autoPrompt?: boolean;
};

function emitGoogleDebug(type: string, detail?: Record<string, unknown>) {
  const store = ((window as any).__runGoogleAuthDebug ??= []) as Array<Record<string, unknown>>;
  const entry = {
    ts: new Date().toISOString(),
    type,
    ...(detail || {}),
  };
  store.push(entry);
  if (store.length > 100) {
    store.splice(0, store.length - 100);
  }
}

type SaasGoogleOwnerAuthResult =
  | { kind: 'owner_token'; ownerAccessToken: string }
  | { kind: 'redirected' }
  | { kind: 'continue_in_app' };

const WORK_ACCOUNT_BRIDGE_TOKEN_MAX_RETRIES = 20;
const WORK_ACCOUNT_BRIDGE_TOKEN_RETRY_DELAY_MS = 100;

async function exchangeSaasGoogleCredentialForOwnerAccessToken(
  idToken: string,
): Promise<SaasGoogleOwnerAuthResult> {
  const authResponse = await fetch('/api/run/saas-auth/google', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  const authPayload = await authResponse.json().catch(() => ({})) as {
    ok?: boolean;
    error?: string;
  };
  if (!authResponse.ok || !authPayload.ok) {
    throw new Error(authPayload.error || 'saas_google_auth_failed');
  }

  const currentUrl = new URL(window.location.href);
  const runTargetHost = currentUrl.searchParams.get('run_target_host')?.trim()
    || (
      shouldUseSaasGoogleOwnerAuth(window.location.search)
        ? window.location.hostname
        : ''
    );
  const returnTo = currentUrl.searchParams.get('return_to')?.trim() || '';
  const bootstrapUrl = new URL('/api/run/saas-bootstrap', window.location.origin);
  if (runTargetHost) {
    bootstrapUrl.searchParams.set('run_target_host', runTargetHost);
    if (runTargetHost === 'run.i.run.ceo') {
      bootstrapUrl.searchParams.set('run_target_tenant', 'run');
    }
  }
  if (returnTo) {
    bootstrapUrl.searchParams.set('return_to', returnTo);
  }

  const bootstrapResponse = await fetch(bootstrapUrl.toString(), {
    method: 'GET',
    credentials: 'same-origin',
  });
  const bootstrapPayload = await bootstrapResponse.json().catch(() => ({})) as {
    ok?: boolean;
    tenantId?: string;
    nextAction?: string;
    ownerAccessToken?: string;
    error?: string;
  };
  let ownerAccessToken = bootstrapPayload.ownerAccessToken?.trim();
  if (
    bootstrapResponse.ok
    && !ownerAccessToken
    && runTargetHost === 'run.i.run.ceo'
    && returnTo.startsWith('/work')
  ) {
    let bridgeTokenAttempted = false;
    let bridgeTokenRequestFailed = false;
    for (
      let attempt = 0;
      attempt < WORK_ACCOUNT_BRIDGE_TOKEN_MAX_RETRIES && !ownerAccessToken;
      attempt += 1
    ) {
      const storageSessionBody = buildStorageSessionRequestBody();
      const authSessionProof = (
        storageSessionBody
        && 'authSessionProof' in storageSessionBody
      )
        ? storageSessionBody.authSessionProof
        : undefined;
      if (!authSessionProof) {
        if (attempt < WORK_ACCOUNT_BRIDGE_TOKEN_MAX_RETRIES - 1) {
          await new Promise((resolve) => window.setTimeout(resolve, WORK_ACCOUNT_BRIDGE_TOKEN_RETRY_DELAY_MS));
        }
        continue;
      }
      bridgeTokenAttempted = true;
      const bridgeTokenResponse = await fetch('/api/run/work/account-bridge-token', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authSessionProof,
          targetTenantId: 'run',
        }),
      });
      const bridgeTokenPayload = await bridgeTokenResponse.json().catch(() => ({})) as {
        ownerAccessToken?: string;
        error?: string;
      };
      if (bridgeTokenResponse.ok) {
        ownerAccessToken = bridgeTokenPayload.ownerAccessToken?.trim();
      } else {
        bridgeTokenRequestFailed = true;
      }
      if (!ownerAccessToken && attempt < WORK_ACCOUNT_BRIDGE_TOKEN_MAX_RETRIES - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, WORK_ACCOUNT_BRIDGE_TOKEN_RETRY_DELAY_MS));
      }
    }
    if (bridgeTokenRequestFailed) {
      throw new Error('work_account_bridge_token_unavailable');
    }
    if (bridgeTokenAttempted && !ownerAccessToken) {
      throw new Error('work_account_bridge_token_missing');
    }
  }
  if (
    bootstrapResponse.ok
    && !ownerAccessToken
    && runTargetHost === 'run.i.run.ceo'
    && returnTo.startsWith('/work')
    && bootstrapPayload.tenantId === 'run'
  ) {
    return { kind: 'continue_in_app' };
  }
  if (!bootstrapResponse.ok || !ownerAccessToken) {
    throw new Error(bootstrapPayload.error || 'saas_owner_access_token_unavailable');
  }

  return { kind: 'owner_token', ownerAccessToken };
}

async function completeWorkGoogleBridgeCredentialAuth(idToken: string): Promise<string | undefined> {
  const bridgeTransactionId = readWorkGoogleBridgeCallbackTransactionId(
    window.location.search,
    window.location.pathname,
    window.location.hostname,
  );
  if (!bridgeTransactionId) {
    throw new Error('work_google_bridge_transaction_missing');
  }

  const authResponse = await fetch('/api/run/saas-auth/google', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, bridgeTransactionId }),
  });
  const authPayload = await authResponse.json().catch(() => ({})) as {
    ok?: boolean;
    error?: string;
  };
  if (!authResponse.ok || !authPayload.ok) {
    throw new Error(authPayload.error || 'work_google_bridge_auth_failed');
  }

  const consumeResponse = await fetch('/api/run/saas-bootstrap/work-google-bridge-transaction/consume', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ bridgeTransactionId }),
  });
  const consumePayload = await consumeResponse.json().catch(() => ({})) as {
    ownerAccessToken?: string;
    transaction?: unknown;
    error?: string;
  };
  if (!consumeResponse.ok) {
    throw new Error(consumePayload.error || 'work_google_bridge_consume_failed');
  }

  const ownerAccessToken = typeof consumePayload.ownerAccessToken === 'string'
    ? consumePayload.ownerAccessToken.trim()
    : '';
  const transaction = readCanonicalWorkGoogleBridgeTransactionTruth(
    consumePayload,
    bridgeTransactionId,
  );
  if (!ownerAccessToken || !transaction) {
    throw new Error('work_google_bridge_truth_missing');
  }

  return buildWorkGoogleBridgeSaasReturnUrl({
    ownerAccessToken,
    transaction,
  });
}

const AuthGoogle: FC<OwnProps> = ({ autoPrompt }) => {
  const { signInWithGoogle } = getActions();
  const isDebugLoginFlow = window.location.search.includes('debug_login=');
  const shouldRenderGoogleAuth = shouldShowGoogleAuth(window.location.search);
  const defaultShouldAutoPrompt = shouldAutoPromptGoogleAuth(window.location.search);
  const saasGoogleAuthBridgeUrl = buildSaasGoogleAuthBridgeUrl(window.location.search);
  const shouldBridgeToSaasGoogleOwnerAuth = Boolean(saasGoogleAuthBridgeUrl);
  const shouldUseSaasOwnerGoogleAuth = shouldUseSaasGoogleOwnerAuth(window.location.search);
  const shouldAutoPrompt = (
    shouldBridgeToSaasGoogleOwnerAuth || shouldUseSaasOwnerGoogleAuth
      ? false
      : autoPrompt ?? defaultShouldAutoPrompt
  );
  const buttonContainerRef = useRef<HTMLDivElement>();
  const hasAutoPromptedRef = useRef(false);
  const hasMountedButtonRef = useRef(false);
  const [isGoogleSdkReady, setIsGoogleSdkReady] = useState(Boolean((window as any).google?.accounts?.id));

  useEffect(() => {
    if (isDebugLoginFlow || !shouldRenderGoogleAuth || shouldBridgeToSaasGoogleOwnerAuth) return;
    if ((window as any).google?.accounts?.id) {
      emitGoogleDebug('sdk_ready_existing');
      setIsGoogleSdkReady(true);
      return;
    }

    const handleLoad = () => {
      emitGoogleDebug('sdk_loaded');
      setIsGoogleSdkReady(true);
    };
    const existingScript = document.getElementById('google-gis-script') as HTMLScriptElement | null;
    if (existingScript) {
      emitGoogleDebug('sdk_wait_existing_script');
      existingScript.addEventListener('load', handleLoad);
      return () => existingScript.removeEventListener('load', handleLoad);
    }

    const script = document.createElement('script');
    script.id = 'google-gis-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad);
    document.head.appendChild(script);
    emitGoogleDebug('sdk_script_injected');
    return () => script.removeEventListener('load', handleLoad);
  }, [isDebugLoginFlow, shouldBridgeToSaasGoogleOwnerAuth, shouldRenderGoogleAuth]);

  useEffect(() => {
    if (isDebugLoginFlow || !shouldRenderGoogleAuth) return undefined;

    const handleFocus = () => emitGoogleDebug('window_focus');
    const handleVisibility = () => emitGoogleDebug('visibility_change', {
      visibilityState: document.visibilityState,
    });

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isDebugLoginFlow, shouldRenderGoogleAuth]);

  const handleCredential = useCallback((credential: string) => {
    emitGoogleDebug('credential_received', {
      credentialLength: credential.length,
      ownerAuth: shouldUseSaasOwnerGoogleAuth,
    });
    if (isWorkGoogleBridgeCallback(window.location.search, window.location.pathname, window.location.hostname)) {
      void (async () => {
        try {
          emitGoogleDebug('work_google_bridge_auth_start');
          const targetUrl = await completeWorkGoogleBridgeCredentialAuth(credential);
          if (!targetUrl) {
            throw new Error('work_google_bridge_target_missing');
          }
          emitGoogleDebug('work_google_bridge_redirect', {
            targetHost: new URL(targetUrl).hostname,
          });
          window.location.replace(targetUrl);
        } catch (err) {
          emitGoogleDebug('work_google_bridge_auth_failed', {
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })();
      return;
    }
    if (shouldUseSaasOwnerGoogleAuth) {
      void (async () => {
        try {
          emitGoogleDebug('saas_owner_auth_start');
          const ownerAuthResult = await exchangeSaasGoogleCredentialForOwnerAccessToken(credential);
          if (ownerAuthResult.kind === 'continue_in_app') {
            emitGoogleDebug('saas_owner_auth_continue_in_app');
            signInWithGoogle({ idToken: credential });
            return;
          }
          if (ownerAuthResult.kind === 'redirected') {
            return;
          }
          const targetUrl = buildSaasGoogleOwnerAuthTargetUrl({
            ownerAccessToken: ownerAuthResult.ownerAccessToken,
          });
          if (!targetUrl) {
            throw new Error('saas_owner_auth_target_unavailable');
          }
          emitGoogleDebug('saas_owner_auth_redirect', {
            targetHost: new URL(targetUrl).hostname,
          });
          window.location.replace(targetUrl);
        } catch (err) {
          emitGoogleDebug('saas_owner_auth_failed', {
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })();
      return;
    }
    signInWithGoogle({ idToken: credential });
  }, [shouldUseSaasOwnerGoogleAuth, signInWithGoogle]);

  const handleSaasGoogleBridgeClick = useCallback(() => {
    if (!saasGoogleAuthBridgeUrl) return;
    emitGoogleDebug('saas_owner_auth_bridge_redirect', {
      targetUrl: saasGoogleAuthBridgeUrl,
    });
    window.location.replace(saasGoogleAuthBridgeUrl);
  }, [saasGoogleAuthBridgeUrl]);

  const handleSaasGoogleBridgeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleSaasGoogleBridgeClick();
  }, [handleSaasGoogleBridgeClick]);

  useEffect(() => {
    if (
      isDebugLoginFlow
      || !shouldRenderGoogleAuth
      || shouldBridgeToSaasGoogleOwnerAuth
      || !isGoogleSdkReady
      || !buttonContainerRef.current
      || hasMountedButtonRef.current
    ) {
      return;
    }

    const mounted = mountGoogleSignInButton({
      google: (window as any).google,
      clientId: GOOGLE_CLIENT_ID,
      container: buttonContainerRef.current,
      onCredential: handleCredential,
      autoPrompt: !hasAutoPromptedRef.current && shouldAutoPrompt,
      onDebugEvent: emitGoogleDebug,
    });
    if (mounted) {
      emitGoogleDebug('button_mounted', {
        autoPrompt: !hasAutoPromptedRef.current && shouldAutoPrompt,
      });
      hasMountedButtonRef.current = true;
      if (shouldAutoPrompt) {
        hasAutoPromptedRef.current = true;
      }
    } else {
      emitGoogleDebug('button_mount_failed', {
        hasClientId: Boolean(GOOGLE_CLIENT_ID),
        hasSdk: Boolean((window as any).google?.accounts?.id),
      });
    }
  }, [
    handleCredential,
    isDebugLoginFlow,
    isGoogleSdkReady,
    shouldAutoPrompt,
    shouldBridgeToSaasGoogleOwnerAuth,
    shouldRenderGoogleAuth,
  ]);

  if (isDebugLoginFlow || !shouldRenderGoogleAuth) {
    return undefined;
  }

  return (
    <div className={styles.root} data-testid={TEST_IDS.loginGoogleButton}>
      <div className={styles.divider}>
        <span>or</span>
      </div>
      <div
        className={styles.googleButtonShell}
        onClick={shouldBridgeToSaasGoogleOwnerAuth ? handleSaasGoogleBridgeClick : undefined}
        onKeyDown={shouldBridgeToSaasGoogleOwnerAuth ? handleSaasGoogleBridgeKeyDown : undefined}
        role={shouldBridgeToSaasGoogleOwnerAuth ? 'button' : undefined}
        tabIndex={shouldBridgeToSaasGoogleOwnerAuth ? 0 : undefined}
      >
        <div ref={buttonContainerRef} className={styles.googleButton} />
        {(shouldBridgeToSaasGoogleOwnerAuth || !isGoogleSdkReady) && (
          <div className={styles.googleButtonFallback}>
            <svg className={styles.googleIcon} viewBox="0 0 24 24" width="18" height="18">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            <span>Sign in with Google</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(AuthGoogle);
